import { boomFinanceApi, BoomFinanceLender } from "api/boom-finance";
import {
  LENDERS_QUERY_KEY,
  lendersQueryKey,
  useFinanceLenders
} from "api/boom-finance/use-lenders";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Radio } from "shared/components";
import { Switch } from "shared/components/forms/Switch";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import {
  Cancel01Icon,
  LinkSquare02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  QUERY_SETTINGS,
  getSettings,
  saveSettings
} from "api/landlord/landlord";
import {
  SUBMIT_BOOM_FINANCE_APPS_TRIGGER_SETTING,
  SUBMIT_BOOM_FINANCE_ON_APPROVAL,
  SUBMIT_BOOM_FINANCE_ON_REVIEW
} from "api/boom-finance/constants";

const SubmissionSettingCard = () => {
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: getSettings
  });

  const currentTrigger = settingsData?.settings?.find(
    (setting) => setting.name === SUBMIT_BOOM_FINANCE_APPS_TRIGGER_SETTING
  )?.value;
  const sendAutomatically = currentTrigger === SUBMIT_BOOM_FINANCE_ON_APPROVAL;

  const { mutate: updateTrigger, isPending } = useMutation({
    mutationKey: [
      QUERY_SETTINGS,
      "UPDATE",
      SUBMIT_BOOM_FINANCE_APPS_TRIGGER_SETTING
    ],
    mutationFn: (trigger: string | null) =>
      saveSettings({ submit_boom_finance_apps_trigger: trigger }),
    onSuccess: () => {
      toast.success("Loan application submission setting updated");
      queryClient.invalidateQueries({ queryKey: [QUERY_SETTINGS] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const disabled = isLoading || isPending;

  return (
    <Card className="w-full p-6 max-w-[800px]">
      <p className="text-base font-bold text-content-1000">
        Loan application submission
      </p>
      <p className="mt-2 text-sm text-content-400">
        Choose how approved applications get sent to the lender.
      </p>

      <div className="mt-4 flex flex-col gap-4">
        <Radio
          id="boom-finance-submission-automatic"
          name="boom-finance-submission"
          label="Send automatically"
          subLabelText="Loan applications are sent to the lender as soon as the applicant is approved."
          checked={sendAutomatically}
          disabled={disabled}
          onChange={() => updateTrigger(SUBMIT_BOOM_FINANCE_ON_APPROVAL)}
        />
        <Radio
          id="boom-finance-submission-review"
          name="boom-finance-submission"
          label="Review before sending"
          subLabelText="You'll review each loan application and send it to the lender yourself."
          checked={!sendAutomatically}
          disabled={disabled}
          onChange={() => updateTrigger(SUBMIT_BOOM_FINANCE_ON_REVIEW)}
        />
      </div>
    </Card>
  );
};

const LenderRow = ({ lender }: { lender: BoomFinanceLender }) => {
  const queryClient = useQueryClient();

  const { mutate: setEnablement, isPending } = useMutation({
    mutationKey: ["BOOM_FINANCE_LENDERS", "UPDATE", lender.id],
    mutationFn: (enabled: boolean) =>
      boomFinanceApi.setLenderEnablement({ lenderId: lender.id, enabled }),
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: LENDERS_QUERY_KEY });
      const previous =
        queryClient.getQueryData<BoomFinanceLender[]>(lendersQueryKey());
      queryClient.setQueryData<BoomFinanceLender[]>(
        lendersQueryKey(),
        (current) =>
          (current ?? []).map((l) =>
            l.id === lender.id ? { ...l, enabled } : l
          )
      );
      return { previous };
    },
    onSuccess: (_data, enabled) => {
      toast.success(
        enabled ? `${lender.name} enabled` : `${lender.name} disabled`
      );
    },
    onError: (err, _enabled, ctx) => {
      if (ctx?.previous)
        queryClient.setQueryData(lendersQueryKey(), ctx.previous);
      toast.error(getErrorMessage(err));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: LENDERS_QUERY_KEY });
    }
  });

  return (
    <li className="py-3 flex items-center gap-3 text-sm text-content-1000 font-medium">
      {lender.logoUrl ? (
        <img
          src={lender.logoUrl}
          alt={lender.name}
          className="size-8 rounded object-contain bg-alpha-40"
        />
      ) : (
        <div className="size-8 rounded bg-alpha-40 flex items-center justify-center text-xs text-content-400">
          {lender.name.charAt(0)}
        </div>
      )}
      <span className="flex-1">{lender.name}</span>
      <Switch
        checked={lender.enabled}
        onChange={(next) => setEnablement(next)}
        disabled={isPending}
      />
    </li>
  );
};

export const BoomFinanceView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();

  const {
    data: lenders = [],
    isLoading: isLoadingLenders,
    isError: isLendersError
  } = useFinanceLenders();

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationKey: ["BOOM_FINANCE", "DELETE"],
    mutationFn: () => boomFinanceApi.disconnect(),
    onSuccess: () => {
      toast.success("BoomFinance integration disconnected successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: openBoomFinance, isPending: isOpeningBoomFinance } =
    useMutation({
      mutationKey: ["BOOM_FINANCE_MAGIC_LINK", "CREATE"],
      mutationFn: () => boomFinanceApi.createMagicLink(),
      onSuccess: ({ url }) => {
        window.open(url, "_blank", "noopener,noreferrer");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "BoomFinance",
                  href: "/settings/integration/boom-finance"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <IntegrationLogoIcon id={IntegrationId.BOOM_FINANCE} size="SM" />
              <p className="text-xl font-bold text-content-1000">BoomFinance</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                HugeIcon={LinkSquare02Icon}
                onClick={() => openBoomFinance()}
                loading={isOpeningBoomFinance}
              >
                Open BoomFinance
              </Button>
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                HugeIcon={Cancel01Icon}
                onClick={() => disconnect()}
                loading={isDisconnecting}
              >
                Disconnect
              </Button>
            </div>
          </div>
        </>
      }
    >
      <SubmissionSettingCard />

      <Card className="w-full p-6 max-w-[800px] mt-4">
        <p className="text-base font-bold text-content-1000">Lenders</p>
        <p className="mt-2 text-sm text-content-400">
          Select the lenders you want enabled for your account
        </p>

        {isLoadingLenders && (
          <p className="mt-4 text-sm text-content-400">Loading lenders…</p>
        )}

        {isLendersError && (
          <p className="mt-4 text-sm text-content-400">
            Failed to load lenders.
          </p>
        )}

        {!isLoadingLenders && !isLendersError && lenders.length === 0 && (
          <p className="mt-4 text-sm text-content-400">No lenders available.</p>
        )}

        {lenders.length > 0 && (
          <ul className="mt-4 divide-y divide-alpha-40">
            {lenders.map((lender) => (
              <LenderRow key={lender.id} lender={lender} />
            ))}
          </ul>
        )}
      </Card>
    </Layout>
  );
};
